import { Chip } from "@mui/material";

export type StatusTone =
  | "neutral"
  | "teal"
  | "green"
  | "orange"
  | "red"
  | "olive"
  | "violet";

/** The tone tokens (tint background + text colour). Exported so a domain surface that
 *  colours by the same semantic tone uses these tokens rather than its own palette.
 *  (The risk matrix's cells and tier chips use the SOLID risk-tier tokens instead:
 *  RiskTier.tsx.) */
export const STATUS_TONE_COLOURS: Record<StatusTone, { backgroundColor: string; color: string }> = {
  neutral: { backgroundColor: "var(--an-border)", color: "var(--an-muted)" },
  teal: {
    backgroundColor: "var(--an-teal-tint)",
    color: "var(--an-teal-dark)",
  },
  green: {
    backgroundColor: "var(--an-green-tint)",
    color: "var(--an-green-text)",
  },
  orange: {
    backgroundColor: "var(--an-orange-tint)",
    color: "var(--an-orange-text)",
  },
  red: { backgroundColor: "var(--an-red-tint)", color: "var(--an-red)" },
  olive: { backgroundColor: "var(--an-olive-tint)", color: "var(--an-olive)" },
  violet: {
    backgroundColor: "var(--an-violet-tint)",
    color: "var(--an-violet)",
  },
};

/**
 * @figma Component 63 · Status Pill (read-only, Chevron=false).
 * https://www.figma.com/design/HDBjSeAYxCpDOHrBaib9ik/Tools?node-id=26-1523
 * Label -> label; semantic Tone -> tone. Domain code supplies both.
 * No dropdown affordance: this component never changes a record.
 */
/** Narrow a config-supplied string to a tone; anything else is not a tone. */
export function isStatusTone(value: unknown): value is StatusTone {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(STATUS_TONE_COLOURS, value);
}

/** The pill's geometry and type, shared with RiskTierChip (same pill, solid colours). */
export const STATUS_CHIP_SX = {
  height: 24,
  borderRadius: "16px",
  fontFamily: '"Roboto-Medium", "Roboto", sans-serif',
  fontWeight: 400,
  fontSize: "13px",
  lineHeight: "18px",
  letterSpacing: "0.06em",
  "& .MuiChip-label": { px: "14px", py: "3px" },
} as const;

export default function StatusChip({
  label,
  tone = "neutral",
}: {
  label: string;
  tone?: StatusTone;
}) {
  return (
    <Chip
      label={label}
      sx={{
        ...STATUS_TONE_COLOURS[tone],
        ...STATUS_CHIP_SX,
      }}
    />
  );
}
